import {msgid, ngettext, t} from 'c-3po';
import {
    BESIDA_ROLE_BUYER, BESIDA_ROLE_COMPANY,
    CHAT_STATUS_ARCHIVE, CHAT_STATUS_BANNED, CHAT_STATUS_ACTIVE,
} from '@evo/chat-core';

import * as c from './../constants';
import * as u from './../utils';
import * as utime from './../date';

export const banMap = {
    [BESIDA_ROLE_COMPANY]: 'clientBanned',
    [BESIDA_ROLE_BUYER]: 'companyBanned'
};

export const CHATS_STATUSES = [
    CHAT_STATUS_ACTIVE, CHAT_STATUS_ARCHIVE, CHAT_STATUS_BANNED
];


export function splitHistoryByDays(history) {
    if (u.isEmpty(history)) return [];
    const splittedHistoryMap = new Map();
    const splittedHistoryArray = [];

    history.forEach((message) => {
        const messageDate = utime.momentInCurrentTimeZone(message.date_sent).format('DD.MM.YYYY');
        const messagesFromHistory = splittedHistoryMap.get(messageDate);

        if (messagesFromHistory) messagesFromHistory.push(message);
        else splittedHistoryMap.set(messageDate, [message]);
    });

    for (const [date, messages] of splittedHistoryMap) splittedHistoryArray.push([date, messages]);

    return splittedHistoryArray;
}

export function sortChatsByRelevance(chats) {
    if (chats.length === 1) return chats;
    // don`t show empty chats, until role will be loaded from rooms meta
    chats = chats.filter((chat) => chat.role);

    const emptyChats = chats.filter((chat) => !chat.date_sent);
    const chatsWithHistory = u.isEmpty(emptyChats) ? chats : chats.filter((chat) => chat.date_sent);

    if (u.isEmpty(chatsWithHistory)) return emptyChats;

    const sortedByDate = chatsWithHistory.sort((a, b) => {
        const aDate = (new Date(a.date_sent)).getTime();
        const bDate = (new Date(b.date_sent)).getTime();

        return bDate - aDate;
    });

    return u.isEmpty(emptyChats) ? sortedByDate : sortedByDate.concat(emptyChats);
}

export function getFilteredChats(chats, status, role) {
    return (chats || []).filter((chat) => (
        chat.role === role && chat.room_status === status
    ));
}

export function withContextOnly(msgArr) {
    if (!msgArr) return false;

    const pureMsgPack = msgArr.filter((msg) => !msg.context_item_id);
    return !Boolean(pureMsgPack.length);
}

export function newMsgCondition(chat) {
    return chat.last_message_counter > chat.last_read_counter && !chat.noMsgSent;
}

export function formDialogStyles(isBannedRoom, hightCoefficient, inputSize) {
    return isBannedRoom ? {
        top: '140px',
        height: `${Math.round(c.SIZE_DIALOG_HEIGHT_WITH_CNTXT * hightCoefficient) - inputSize}px`,
        minHeight:
            `${Math.round(c.SIZE_DIALOG_HEIGHT_WITH_CNTXT * hightCoefficient) - inputSize}px`,
    } : {
        top: '70px',
        height: `${Math.round(c.SIZE_DIALOG_HEIGHT * hightCoefficient) - inputSize + 35}px`,
        minHeight: `${Math.round(c.SIZE_DIALOG_HEIGHT * hightCoefficient) - inputSize + 35}px`,
    };
}

export function getSplitLineDate(msgDate) {
    const TODAY = utime.momentInCurrentTimeZone().format('DD.MM.YYYY');
    const YESTERDAY = utime.momentInCurrentTimeZone().subtract(1, 'day').format('DD.MM.YYYY');
    switch (true) {
        case (YESTERDAY === msgDate):
            return c.CHAT_HISTORY_YESTERDAY;
        case (TODAY === msgDate):
            return c.CHAT_HISTORY_TODAY;
        default:
            return utime.momentInCurrentTimeZone(msgDate, 'DD.MM.YYYY').format('DD MMMM');
    }
}

export function humanizeChatDate(date, withHours = false) {
    if (!date) return 'error';

    const msgDate = utime.currentTimeZoneInMilisec(date);
    const currentDate = new Date;

    const timeDifference = currentDate - msgDate;
    const diffInMinutes = Math.round(timeDifference / c.MINUTE_IN_MILISEC);
    const diffInHours = Math.round(timeDifference / c.HOUR_IN_MILISEC);

    switch (true) {
        case (diffInMinutes === 0):
            return t`только что`;
        case (diffInMinutes > 0 && diffInMinutes < 60):
            return ngettext(msgid`${diffInMinutes} минуту назад`,
                    `${diffInMinutes} минуты назад`,
                    `${diffInMinutes} минут назад`,
                    diffInMinutes);
        case (withHours && (diffInHours > 0 && diffInHours < 24)):
            return ngettext(msgid`${diffInHours} час назад`,
                    `${diffInHours} часа назад`,
                    `${diffInHours} часов назад`,
                    diffInHours);
        default:
            return utime.momentInCurrentTimeZone(date)
                     .format(!withHours ? 'HH:mm' : 'DD.MM.YYYY HH:mm');
    }
}

export function formatChatListDate(msgDate) {
    if (utime.isDayToday(msgDate)) return utime.momentInCurrentTimeZone(msgDate).format('HH:mm');
    return utime.momentInCurrentTimeZone(msgDate).format('DD.MM.YYYY');
}

export function truncatePhrase(phrase, maxLength, endSymbol = '...') {
    if (phrase.length < maxLength) return phrase;
    return `${phrase.slice(0, maxLength)} ${endSymbol}`;
}

export function getElementScrollBotPosition(elem) {
    const scrollHeight = elem.scrollHeight;
    const elementHeight = elem.offsetHeight;

    return scrollHeight - elementHeight;
}

export function blockOuterScroll(e, area) {
    if (!area) return void 0;

    const delta = e.deltaY || e.detail || e.wheelDelta;

    if (delta < 0 && area.scrollTop === 0) e.preventDefault();

    if (delta > 0 && area.scrollHeight - area.clientHeight - area.scrollTop <= 1) {
        e.preventDefault();
    }

    return void 0;
}

export function getUserName(user, roomExtra) {
    let name;
    if (user.anonymous) {
        name = t`Анонимный пользователь`;
    } else if (roomExtra.company_type === c.COMPANY_C2C_TYPE && user.nickname) {
        return user.nickname;
    } else if (user.name) {
        name = user.name;
    } else if (roomExtra.company_name && user.role === BESIDA_ROLE_COMPANY) {
        name = roomExtra.company_name;
    } else if (!roomExtra.company_name && user.role === BESIDA_ROLE_COMPANY) {
        name = t`Компания`;
    } else if (user.role === BESIDA_ROLE_BUYER) {
        name = t`Покупатель`;
    }
    return name;
}

export function getChatsStatusText(chatsStatus) {
    switch (chatsStatus) {
        case CHAT_STATUS_ACTIVE:
            return t`Активные чаты`;
        case CHAT_STATUS_ARCHIVE:
            return t`Архив чатов`;
        case CHAT_STATUS_BANNED:
            return t`Заблокированные чаты`;
        default:
            return null;
    }
}
